import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useBlockCommunityUserMutation } from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { AuthorId } from '../model/post.types';

type BlockUserArgs = {
  communityId: CommunityId;
  userId: AuthorId;
};

export const useBlockUser = () => {
  const [blockUserMutation] = useBlockCommunityUserMutation();

  return useCallback(
    ({ communityId, userId }: BlockUserArgs) =>
      blockUserMutation({
        variables: { communityId, userId },
        optimisticResponse: {
          __typename: 'Mutation',
          blockUser: true,
        },
        update: (cache) => {
          cache.modify({
            fields: {
              blockedUsers: (
                // The resolver returns null, not [], for a community with no
                // blocked users.
                existing: readonly Reference[] | null = [],
                { storeFieldName, readField, toReference },
              ) => {
                if (!storeFieldName.includes(`"communityId":${communityId}`)) {
                  return existing;
                }
                const current = existing ?? [];
                if (current.some((ref) => readField('id', ref) === userId)) {
                  return current;
                }
                const blockedUserRef = toReference(
                  { __typename: 'BlockedUser', id: userId },
                  true,
                );
                return blockedUserRef ? [...current, blockedUserRef] : current;
              },
            },
          });
          cache.modify({
            id: cache.identify({ __typename: 'Community', id: communityId }),
            fields: {
              blockedUsersCount: (count: number) => count + 1,
            },
          });
        },
      })
        .then(() => {
          toast.success('User blocked');
        })
        .catch(() => {
          toast.error('Something went wrong');
        }),
    [blockUserMutation],
  );
};
